/**
 * PublicIntakePage — orchestrateur `/intake/:token` (Chunk 9.N.2 v2).
 *
 * Orchestre :
 *   1. Détection langue request-time (`?lang=fr|en` > LanguageDetector
 *      par défaut localStorage > navigator > `"fr"`). Force `changeLanguage`
 *      si query param présent et différent de la langue courante.
 *   2. GET metadata via `usePublicIntakeMetadata(token)` — dispatch :
 *        - `kind:"valid"` → `<IntakeForm>`
 *        - `kind:"invalid"` ou `"error"` → `<InvalidState>`
 *        - `isLoading` → `<LoadingSkeleton>` (anti-leak : zéro métadonnée)
 *   3. POST callback :
 *        - submission `received` → bascule local state vers `<SuccessState>`
 *        - submission `invalid` → bascule local state vers `<InvalidState>`
 *          (anti-leak — race quota détectée post-claim)
 *        - autres erreurs → toast inline géré par `<IntakeForm>`
 *
 * **Tous les états sont rendus dans `<IntakeChrome>`** — coque visuelle
 * partagée garantissant la parité anti-énumération du cadrage F2.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { appliquerLangueEphemere } from "../../lib/localeEphemere";
import { usePublicIntakeMetadata } from "../../lib/queries/public-intake";

import { IntakeChrome } from "./_parts/IntakeChrome";
import { IntakeForm } from "./_parts/IntakeForm";
import { IntakeFormFill } from "./_parts/IntakeFormFill";
import { InvalidState } from "./_parts/InvalidState";
import { SuccessState } from "./_parts/SuccessState";

export function PublicIntakePage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  // A receipt belongs only to the link submitted in this mounted page.
  return <PublicIntakeSession key={token} token={token} />;
}

function PublicIntakeSession({ token }: { token: string | undefined }): JSX.Element {
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();

  // Langue forcée via ?lang=fr|en (cadrage F2 §3.6 — priorité 1).
  const queryLang = searchParams.get("lang");
  const metadataQuery = usePublicIntakeMetadata(token);

  /**
   * 9.BJ.6 — UNE seule autorité de langue pour toute la page.
   *
   * Le rendu du schéma suivait déjà la langue du serveur (9.BJ.3), mais la coque
   * — titre, sous-titre, consentement Loi 25, bouton d'envoi — suivait i18next,
   * dont la langue vient du navigateur. Un mandat français s'ouvrait donc avec
   * « Form to complete » au-dessus de son texte français.
   *
   * L'ordre est celui du cadrage F2, complété d'un cran :
   *
   *   ?lang=  >  langue renvoyée par le SERVEUR  >  navigateur/localStorage  >  fr
   *
   * Le serveur s'intercale parce qu'il sait ce que la page contient : un gabarit
   * qui déclare `default_language` n'existe que dans cette langue.
   *
   * **Aucune persistance.** Une langue IMPOSÉE par un document n'est pas une
   * préférence exprimée : la mémoriser ferait basculer l'interface de quelqu'un
   * qui n'a rien demandé. Le sélecteur de la coque, lui, reste un choix et
   * continue de se souvenir.
   */
  const langueServeur =
    metadataQuery.data?.kind === "form" || metadataQuery.data?.kind === "valid"
      ? metadataQuery.data.kind === "form"
        ? metadataQuery.data.language
        : metadataQuery.data.metadata.language
      : null;
  const langueVoulue: "fr" | "en" | null =
    queryLang === "fr" || queryLang === "en" ? queryLang : langueServeur;

  useEffect(() => {
    if (langueVoulue === null) return;
    void appliquerLangueEphemere(i18n, langueVoulue);
  }, [langueVoulue, i18n]);
  const [receipt, setReceipt] = useState<{
    intakeEventId: string;
    kind: "form" | "documents";
  } | null>(null);
  const [postForceInvalid, setPostForceInvalid] = useState(false);

  let body: JSX.Element;

  if (receipt) {
    // Only a successful POST supplies this local receipt. A subsequent metadata
    // refetch may see the consumed link; it cannot undo the confirmed submission.
    body = <SuccessState {...receipt} />;
  } else if (metadataQuery.isLoading) {
    body = <LoadingSkeleton />;
  } else if (
    !metadataQuery.data ||
    metadataQuery.data.kind === "invalid" ||
    metadataQuery.data.kind === "error" ||
    postForceInvalid
  ) {
    // Anti-leak fallback — réseau / shape / 4xx/5xx tous mappés vers Invalid.
    body = <InvalidState />;
  } else if (metadataQuery.data.kind === "form") {
    // 9.U.5 — lien versionné : formulaire structuré (data-only), zéro métadonnée
    // projet/tenant exposée. token défini (query enabled + kind:"form").
    body = (
      <IntakeFormFill
        token={token!}
        schema={metadataQuery.data.schema}
        language={metadataQuery.data.language}
        prefill={metadataQuery.data.prefill}
        onSuccess={(intakeEventId) => setReceipt({ intakeEventId, kind: "form" })}
        onTokenInvalid={() => setPostForceInvalid(true)}
      />
    );
  } else {
    // metadata.kind === "valid" → render le formulaire legacy (upload documents).
    // (token est défini ici car metadataQuery `enabled: !!token` + `kind:"valid"`
    // implique que le query a tourné avec un token valide.)
    body = (
      <IntakeForm
        token={token!}
        projectTitle={metadataQuery.data.metadata.project.title}
        onSuccess={(intakeEventId) => setReceipt({ intakeEventId, kind: "documents" })}
        onTokenInvalid={() => setPostForceInvalid(true)}
      />
    );
  }

  return <IntakeChrome>{body}</IntakeChrome>;
}

/**
 * Skeleton loading — anti-leak strict (aucune métadonnée projet exposée
 * tant que le query n'a pas confirmé la validité du token).
 *
 * `motion-reduce:animate-none` désactive le pulse sous prefers-reduced-motion.
 * `aria-live="polite"` annonce « Chargement… » aux lecteurs d'écran sans
 * voler le focus.
 */
function LoadingSkeleton(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <p className="sr-only" role="status" aria-live="polite">
        {t("public_intake.page.loading")}
      </p>
      <div className="space-y-2">
        <div className="h-8 w-2/3 animate-pulse rounded bg-slate-200/70 motion-reduce:animate-none" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-slate-200/60 motion-reduce:animate-none" />
      </div>
      <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] sm:p-7">
        <div className="h-44 animate-pulse rounded-xl bg-slate-100 motion-reduce:animate-none" />
        <div className="h-4 w-full animate-pulse rounded bg-slate-100 motion-reduce:animate-none" />
        <div className="h-4 w-5/6 animate-pulse rounded bg-slate-100 motion-reduce:animate-none" />
        <div className="h-10 w-full animate-pulse rounded-md bg-slate-200/70 motion-reduce:animate-none" />
      </div>
    </div>
  );
}
